(() => {
  const instances = new WeakMap();

  function initialize(root) {
    if (!root || instances.has(root)) return;
    const packs = [...root.querySelectorAll('[data-wallet-pack]')];
    const slots = [...root.querySelectorAll('[data-wallet-color-slot]')];
    const quantityInput = root.querySelector('[data-wallet-quantity]');
    const productForm = root.querySelector('.dt-wallet__buy product-form');
    const form = productForm?.querySelector('form');
    const submitButton = productForm?.querySelector('.product-form__submit');
    const errorNode = root.querySelector('[data-wallet-error]');
    const sticky = root.querySelector('[data-wallet-sticky]');
    const stickyButton = root.querySelector('[data-wallet-sticky-add]');
    const stickyPrice = root.querySelector('[data-wallet-sticky-price]');
    const stickySubtotal = root.querySelector('[data-wallet-sticky-subtotal]');
    const nativePrice = root.querySelector('[data-wallet-native-price]');
    const bundlePrice = root.querySelector('[data-wallet-bundle-price]');
    const topTotal = root.querySelector('[data-wallet-top-total]');
    const topCompare = root.querySelector('[data-wallet-top-compare]');
    const topTag = root.querySelector('[data-wallet-top-tag]');
    const showBogoOffer = root.dataset.walletBogo === 'true';
    const showCompare = root.dataset.walletShowCompare !== 'false';
    const linkedId = (pack) => (pack?.dataset.walletVariantId || '').trim();
    const tagFor = (amounts, mode, label) => {
      if (!amounts || mode === 'none') return '';
      const percent = amounts.compare > amounts.total && amounts.compare > 0
        ? Math.round((amounts.compare - amounts.total) * 100 / amounts.compare) : 0;
      if (mode !== 'custom' && percent <= 0) return '';
      return (label || (mode === 'custom' ? '' : 'Save [percent]%')).replaceAll('[percent]', percent);
    };
    let initialVariant = null;
    let variants = [];
    let cartQuantities = new Map();
    let submitting = false;
    let destroyed = false;
    let disabledSnapshot = [];
    let submitWasDisabled = false;
    let nativeSubmitDisabled = Boolean(submitButton?.disabled);
    let primaryPending = false;

    try {
      initialVariant = JSON.parse(root.querySelector('[data-wallet-initial-variant]')?.textContent || 'null');
      variants = JSON.parse(root.querySelector('[data-wallet-variants]')?.textContent || '[]');
      for (const [id, quantity] of JSON.parse(root.querySelector('[data-wallet-cart-quantities]')?.textContent || '[]')) {
        const key = String(id);
        cartQuantities.set(key, (cartQuantities.get(key) || 0) + Number(quantity || 0));
      }
    } catch (_) {
      variants = [];
      cartQuantities = new Map();
    }
    const variantById = new Map(variants.map((variant) => [String(variant.id), variant]));
    const currency = root.dataset.walletCurrency || 'USD';
    const formatPrice = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency })
          .format(Number(cents || 0) / 100);
      } catch (_) {
        return (Number(cents || 0) / 100).toFixed(2) + ' ' + currency;
      }
    };
    const jsUrl = (path) => String(path).replace(/\/$/, '').replace(/\.js$/, '') + '.js';
    const cartUrl = window.routes?.cart_url || (window.Shopify?.routes?.root || '/') + 'cart';
    const cartAddUrl = window.routes?.cart_add_url || (window.Shopify?.routes?.root || '/') + 'cart/add';
    const selectedVariant = () => {
      const node = root.querySelector('variant-selects [data-selected-variant]');
      if (!node) return initialVariant;
      try { return JSON.parse(node.textContent || 'null'); } catch (_) { return null; }
    };
    const currentVariant = () => {
      const selected = selectedVariant();
      if (!selected) return null;
      const catalog = variantById.get(String(selected.id)) || {};
      return {
        ...catalog,
        ...selected,
        inventory_quantity: selected.inventory_quantity == null ? catalog.inventory_quantity : selected.inventory_quantity,
      };
    };
    const selectedPack = () => packs.find((pack) => pack.checked);
    const countInCart = (id) => cartQuantities.get(String(id)) || 0;
    const availableUnits = (variant) => {
      if (!variant?.available) return 0;
      const rule = variant.quantity_rule || {};
      let amount = rule.max == null ? Infinity : Number(rule.max);
      if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny'
        && typeof variant.inventory_quantity === 'number') {
        amount = Math.min(amount, variant.inventory_quantity - countInCart(variant.id));
      }
      return Math.max(0, amount);
    };
    const packSlots = (pack) => [...(pack?.closest('.dt-wallet__pack')?.querySelectorAll('[data-wallet-color-slot]') || [])];
    const selectedItems = (pack) => {
      if (linkedId(pack)) return variantById.has(linkedId(pack)) ? [{ id: linkedId(pack), quantity: 1 }] : null;
      const selects = packSlots(pack);
      const ids = selects.length
        ? selects.map((select) => select.value)
        : [String(currentVariant()?.id || '')];
      if (ids.some((id) => !id)) return null;
      const counts = new Map();
      ids.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
      return [...counts].map(([id, quantityForColor]) => ({ id, quantity: quantityForColor }));
    };
    const validateItems = (items) => {
      if (!items?.length) return 'Choose a color for each wallet.';
      for (const item of items) {
        const variant = String(currentVariant()?.id) === item.id ? currentVariant() : variantById.get(item.id);
        if (!variant?.available) return 'One of the selected colors is unavailable.';
        const rule = variant.quantity_rule || {};
        const minimum = Number(rule.min || 1);
        const increment = Number(rule.increment || 1);
        const maximum = rule.max == null ? Infinity : Number(rule.max);
        if (item.quantity < minimum || item.quantity > maximum
          || (item.quantity - minimum) % increment !== 0) {
          return 'This color is not available in the selected quantity.';
        }
        if (item.quantity > availableUnits(variant)) {
          return 'Not enough stock is available in one of the selected colors.';
        }
      }
      return '';
    };
    const showError = (message = '') => {
      if (!errorNode) return;
      errorNode.textContent = message;
      errorNode.hidden = !message;
    };
    const totalsFor = (pack) => {
      const quantity = Number(pack?.value || 1);
      const linked = linkedId(pack);
      if (linked && !variantById.has(linked)) return null;
      const selects = packSlots(pack);
      const picked = linked ? [variantById.get(linked)] : selects.length
        ? selects.map((select) => variantById.get(String(select.value)))
        : [currentVariant()];
      if (picked.some((variant) => !variant)) return null;
      const prices = picked.map((variant) => Number(variant.price));
      if (prices.some((price) => !Number.isFinite(price))) return null;
      const subtotal = prices.reduce((sum, price) => sum + price, 0);
      const freeUnits = showBogoOffer && quantity > 1 ? Math.floor(prices.length / 2) : 0;
      const discount = [...prices].sort((a, b) => a - b).slice(0, freeUnits)
        .reduce((sum, price) => sum + price, 0);
      const total = subtotal - discount;
      const regularTotal = picked.reduce((sum, variant, index) => {
        const compare = Number(variant.compare_at_price);
        return sum + (Number.isFinite(compare) && compare > prices[index] ? compare : prices[index]);
      }, 0);
      const compare = freeUnits ? subtotal : regularTotal;
      let tag = '';
      if (freeUnits) {
        tag = subtotal > 0 ? `Save ${Math.round((discount / subtotal) * 100)}%` : 'Offer';
      } else if (compare > total) {
        tag = 'Sale';
      }
      return { subtotal, total, compare, tag };
    };
    const syncFirstSlots = (id) => {
      slots.filter((select) => Number(select.dataset.walletColorSlot) === 1).forEach((select) => {
        if ([...select.options].some((option) => option.value === String(id))) select.value = String(id);
      });
    };
    const syncNativePrimary = (id) => {
      if (String(currentVariant()?.id) === String(id)) return true;
      const variant = variantById.get(String(id));
      const picker = root.querySelector('variant-selects');
      if (!variant) return false;
      if (!picker) {
        initialVariant = variant;
        const input = form?.querySelector('input[name="id"]');
        if (input) { input.value = String(variant.id); input.disabled = !variant.available; }
        nativeSubmitDisabled = !variant.available;
        primaryPending = false;
        return true;
      }
      if (variant.options?.length !== 1) return false;
      const value = variant.options[0];
      const radio = [...picker.querySelectorAll('input[type="radio"]')].find((input) => input.value === value);
      if (radio) {
        primaryPending = true;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
      const select = [...picker.querySelectorAll('select')].find((item) => [...item.options].some((option) => option.value === value));
      if (!select) return false;
      primaryPending = true;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };
    const update = () => {
      if (submitting) {
        if (stickyButton) stickyButton.disabled = true;
        return;
      }
      const first = variantById.get(linkedId(selectedPack())) || currentVariant();
      slots.forEach((select) => {
        select.disabled = !form;
        [...select.options].forEach((option) => { option.disabled = !variantById.get(String(option.value))?.available; });
        const image = select.closest('.dt-wallet__color-choice')?.querySelector('[data-wallet-slot-image]');
        const imageUrl = select.selectedOptions[0]?.dataset.walletImage;
        if (image) {
          image.hidden = !imageUrl;
          if (imageUrl && image.getAttribute('src') !== imageUrl) image.src = imageUrl;
        }
      });
      packs.forEach((pack) => {
        pack.disabled = !form || Boolean(linkedId(pack) && !variantById.get(linkedId(pack))?.available);
        const card = pack.closest('.dt-wallet__pack');
        const total = card?.querySelector('[data-wallet-pack-total]');
        const compared = card?.querySelector('[data-wallet-pack-compare]');
        const tag = card?.querySelector('[data-wallet-pack-tag]');
        const amounts = totalsFor(pack);
        if (total) total.textContent = amounts == null ? (linkedId(pack) ? 'Unavailable variant' : 'Choose colors') : formatPrice(amounts.total);
        if (compared) {
          compared.hidden = card.dataset.walletShowCompare === 'false' || !amounts || amounts.compare <= amounts.total;
          if (amounts) compared.textContent = formatPrice(amounts.compare);
        }
        if (tag) {
          const mode = card.dataset.walletBadgeMode || 'auto';
          const label = mode === 'custom' ? card.dataset.walletBadgeText : root.dataset.walletPackBadge;
          tag.textContent = tagFor(amounts, mode, label);
          tag.hidden = !tag.textContent;
        }
      });
      let chosen = selectedPack();
      if (!chosen) chosen = packs.find((pack) => !pack.disabled);
      if (chosen) chosen.checked = true;
      const quantity = Number(chosen?.value || 1);
      if (quantityInput) quantityInput.value = String(quantity);
      const amounts = totalsFor(chosen);
      if (nativePrice) nativePrice.hidden = true;
      if (bundlePrice) bundlePrice.hidden = false;
      if (topTotal) topTotal.textContent = amounts == null ? 'Choose colors' : formatPrice(amounts.total);
      if (topCompare) {
        topCompare.hidden = !showCompare || !amounts || amounts.compare <= amounts.total;
        if (amounts) topCompare.textContent = formatPrice(amounts.compare);
      }
      if (topTag) {
        topTag.textContent = tagFor(amounts, root.dataset.walletBadgeMode || 'auto', root.dataset.walletBadgeText);
        topTag.hidden = !topTag.textContent;
      }
      if (stickyPrice) stickyPrice.textContent = amounts == null ? 'Choose colors' : formatPrice(amounts.total);
      if (stickySubtotal) {
        stickySubtotal.hidden = !showCompare || !amounts || amounts.compare <= amounts.total;
        if (amounts) stickySubtotal.textContent = formatPrice(amounts.compare);
      }
      const items = selectedItems(chosen);
      const unavailable = Boolean(validateItems(items));
      const usingLinkedVariant = Boolean(linkedId(chosen));
      if (submitButton) submitButton.disabled = (!usingLinkedVariant && (nativeSubmitDisabled || primaryPending)) || unavailable || (packs.length > 0 && !chosen);
      if (stickyButton) {
        stickyButton.textContent = !unavailable && first?.available
          ? stickyButton.dataset.walletLabelAvailable
          : stickyButton.dataset.walletLabelSoldOut;
        stickyButton.disabled = submitting || Boolean(submitButton?.disabled);
      }
    };
    const readCart = async () => {
      const response = await fetch(jsUrl(cartUrl), { credentials: 'same-origin' });
      if (!response.ok) throw new Error('cart_unavailable');
      const data = await response.json();
      if (!Array.isArray(data.items)) throw new Error('cart_unavailable');
      const next = new Map();
      data.items.forEach((item) => {
        const key = String(item.variant_id);
        next.set(key, (next.get(key) || 0) + Number(item.quantity || 0));
      });
      cartQuantities = next;
      return next;
    };
    const setLoading = (loading) => {
      submitting = loading;
      if (loading) {
        const controls = [...root.querySelectorAll('.dt-wallet__variants input, .dt-wallet__variants select, [data-wallet-pack], [data-wallet-color-slot]')];
        disabledSnapshot = controls.map((control) => [control, control.disabled]);
        controls.forEach((control) => { control.disabled = true; });
        submitWasDisabled = Boolean(submitButton?.disabled);
        if (submitButton) submitButton.disabled = true;
      } else {
        disabledSnapshot.forEach(([control, disabled]) => { control.disabled = disabled; });
        disabledSnapshot = [];
        if (submitButton) submitButton.disabled = submitWasDisabled;
      }
      submitButton?.classList.toggle('loading', loading);
      submitButton?.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      update();
    };
    const onBundleSubmit = async (event) => {
      if (event.target !== form) return;
      if (submitting) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const quantity = Number(selectedPack()?.value || 1);
      if (quantity <= 1 && !linkedId(selectedPack())) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const items = selectedItems(selectedPack());
      showError();
      setLoading(true);
      let before = new Map(cartQuantities);
      let postAttempted = false;
      let addSucceeded = false;
      try {
        before = new Map(await readCart());
        const problem = validateItems(items);
        if (problem) { showError(problem); return; }
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender?.().map((section) => section.id);
        const payload = { items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })) };
        if (sections?.length) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
          drawer.setActiveElement?.(document.activeElement);
        }
        postAttempted = true;
        const response = await fetch(jsUrl(cartAddUrl), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          const after = await readCart();
          const changed = items.some((item) => (after.get(item.id) || 0) > (before.get(item.id) || 0));
          if (changed) {
            window.location.assign(cartUrl);
          } else {
            showError(data.description || 'The selected set could not be added. Review your colors and try again.');
          }
          return;
        }
        addSucceeded = true;
        await readCart();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'drivtown-wallet', cartData: data });
        }
        if (drawer && data.sections?.['cart-drawer'] && data.sections?.['cart-icon-bubble']) {
          drawer.renderContents(data);
        } else {
          window.location.assign(cartUrl);
        }
      } catch (_) {
        if (addSucceeded) {
          window.location.assign(cartUrl);
          return;
        }
        try {
          const after = await readCart();
          const changed = [...after].some(([id, count]) => count > (before.get(id) || 0));
          if (changed) {
            window.location.assign(cartUrl);
          } else {
            showError(postAttempted
              ? 'The selected set could not be added. Review your cart before trying again.'
              : 'The cart could not be checked. Please try again.');
          }
        } catch (_) {
          if (postAttempted) window.location.assign(cartUrl);
          else showError('The cart could not be checked. Please try again.');
        }
      } finally {
        if (!destroyed) setLoading(false);
      }
    };
    const onChange = (event) => {
      if (event.target.matches('[data-wallet-color-slot]')) {
        const pack = event.target.closest('.dt-wallet__pack')?.querySelector('[data-wallet-pack]');
        if (pack) pack.checked = true;
        if (Number(event.target.dataset.walletColorSlot) === 1) {
          if (syncNativePrimary(event.target.value)) syncFirstSlots(event.target.value);
          else {
            syncFirstSlots(currentVariant()?.id);
            showError('Choose the first color using the main color selector.');
            update();
            return;
          }
        }
      }
      if (event.target.matches('[data-wallet-pack], [data-wallet-color-slot], .product-variant-id')) {
        if (event.target.matches('.product-variant-id')) {
          const id = event.target.value;
          if (variantById.has(String(id))) syncFirstSlots(id);
        }
        showError();
        update();
      }
    };
    const onStickyClick = () => {
      if (submitButton && !submitButton.disabled && !submitting) submitButton.click();
    };
    const onVideoPlay = (event) => {
      if (!event.target.matches('.dt-wallet__reel video, .dt-wallet__story-media video')) return;
      root.querySelectorAll('.dt-wallet__reel video, .dt-wallet__story-media video').forEach((video) => {
        if (video !== event.target) video.pause();
      });
    };

    root.addEventListener('change', onChange);
    root.addEventListener('submit', onBundleSubmit, true);
    root.addEventListener('play', onVideoPlay, true);
    stickyButton?.addEventListener('click', onStickyClick);
    update();

    let observer = null;
    let thumbnailObserver = null;
    let posterObserver = null;
    let videoObserver = null;
    const videos = [...root.querySelectorAll('video[data-wallet-poster]')];
    const previewEnabled = root.dataset.walletVideoPreview === 'true';
    const visibility = new Map();
    const manualVideos = new WeakSet();
    const previewVideos = new WeakSet();
    const updatePreview = () => {
      if (!previewEnabled) return;
      const allowed = !document.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches && !navigator.connection?.saveData;
      const manualPlaying = videos.some((video) => manualVideos.has(video) && !video.paused && (visibility.get(video) || 0) > 0);
      const candidate = allowed && !manualPlaying ? videos.filter((video) => !manualVideos.has(video) && (visibility.get(video) || 0) >= 0.5).sort((a, b) => visibility.get(b) - visibility.get(a))[0] : null;
      videos.forEach((video) => {
        if (video === candidate) {
          if (!previewVideos.has(video)) { video.muted = true; video.loop = true; previewVideos.add(video); }
          if (video.paused) video.play().catch(() => {});
        } else if (previewVideos.has(video) && !manualVideos.has(video)) video.pause();
      });
    };
    const onVideoInteract = (event) => {
      if (videos.includes(event.target)) manualVideos.add(event.target);
    };
    root.addEventListener('pointerdown', onVideoInteract, true);
    root.addEventListener('keydown', onVideoInteract, true);
    document.addEventListener('visibilitychange', updatePreview);
    const revealPoster = (video) => {
      video.poster = video.dataset.walletPoster;
      video.removeAttribute('data-wallet-poster');
    };
    if ('IntersectionObserver' in window) {
      posterObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          revealPoster(target);
          posterObserver.unobserve(target);
        });
      }, { rootMargin: '300px' });
      videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting && !target.paused) target.pause();
        });
        entries.forEach(({ target, intersectionRatio }) => visibility.set(target, intersectionRatio));
        updatePreview();
      }, { threshold: [0, 0.5, 0.75, 1] });
      videos.forEach((video) => { posterObserver.observe(video); videoObserver.observe(video); });
    } else videos.forEach(revealPoster);
    let cartUpdateUnsubscriber = null;
    let variantChangeUnsubscriber = null;
    let cartRequestRevision = 0;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== root.dataset.section || destroyed) return;
        primaryPending = false;
        nativeSubmitDisabled = Boolean(submitButton?.disabled);
        if (data.variant?.id) syncFirstSlots(data.variant.id);
        const nextUrl = new URL(window.location.href);
        if (nextUrl.pathname !== new URL(root.dataset.url, nextUrl.origin).pathname) return;
        if (data.variant?.id) nextUrl.searchParams.set('variant', data.variant.id);
        else nextUrl.searchParams.delete('variant');
        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}`);
        root.querySelector('share-button')?.updateUrl(nextUrl.href);
        update();
      });
      cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
        const revision = ++cartRequestRevision;
        try {
          await readCart();
          if (revision === cartRequestRevision && !destroyed) update();
        } catch (_) {
          // The Shopify cart remains the source of truth if it cannot be refreshed.
        }
      });
    }
    const thumbnailList = root.querySelector('media-gallery .thumbnail-list');
    if (thumbnailList && 'MutationObserver' in window) {
      const revealActiveThumbnail = () => {
        if (!window.matchMedia('(min-width: 990px)').matches) return;
        const active = thumbnailList.querySelector('button[aria-current="true"]')?.closest('.thumbnail-list__item');
        if (!active) return;
        const viewport = thumbnailList.getBoundingClientRect();
        const item = active.getBoundingClientRect();
        if (item.top < viewport.top) thumbnailList.scrollTop += item.top - viewport.top;
        else if (item.bottom > viewport.bottom) thumbnailList.scrollTop += item.bottom - viewport.bottom;
      };
      thumbnailObserver = new MutationObserver(revealActiveThumbnail);
      thumbnailObserver.observe(thumbnailList, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });
      revealActiveThumbnail();
    }
    if (sticky && submitButton && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        sticky.inert = !show;
      });
      observer.observe(submitButton);
    }
    instances.set(root, () => {
      destroyed = true;
      root.removeEventListener('change', onChange);
      root.removeEventListener('submit', onBundleSubmit, true);
      root.removeEventListener('play', onVideoPlay, true);
      stickyButton?.removeEventListener('click', onStickyClick);
      observer?.disconnect();
      thumbnailObserver?.disconnect();
      posterObserver?.disconnect();
      videoObserver?.disconnect();
      root.removeEventListener('pointerdown', onVideoInteract, true);
      root.removeEventListener('keydown', onVideoInteract, true);
      document.removeEventListener('visibilitychange', updatePreview);
      videos.forEach((video) => video.pause());
      cartRequestRevision += 1;
      variantChangeUnsubscriber?.();
      cartUpdateUnsubscriber?.();
    });
  }

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-wallet-root]')) initialize(scope);
    scope.querySelectorAll?.('[data-wallet-root]').forEach(initialize);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-wallet-root]')
      ? event.target
      : event.target.querySelector?.('[data-wallet-root]');
    instances.get(root)?.();
    if (root) instances.delete(root);
  });
})();
